'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnglePanel } from '@/components/AnglePanel';
import { FeedbackPanel } from '@/components/FeedbackPanel';
import { PoseCanvas } from '@/components/PoseCanvas';
import { ScoreRing } from '@/components/ScoreRing';
import { StatsBar } from '@/components/StatsBar';
import { ExerciseSelector } from '@/components/ExerciseSelector';
import { ThemeToggle } from '@/components/ThemeToggle';
import { env } from '@/lib/env';
import { exerciseLabel } from '@/lib/exercises';
import { PoseData } from '@/lib/types';

const ANGLE_LABELS: Record<string, string> = {
  left_knee: '左膝', right_knee: '右膝',
  left_hip: '左髋',  right_hip: '右髋',
  left_elbow: '左肘', right_elbow: '右肘',
  spine: '脊柱',
};

export default function Home() {
  const [poseData, setPoseData]     = useState<PoseData | null>(null);
  const [connected, setConnected]   = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [cameraOn, setCameraOn]     = useState(false);
  const [cameraErr, setCameraErr]   = useState<string | null>(null);
  const [selectedExercise, setSelectedExercise] = useState('squat');

  const wsRef                 = useRef<WebSocket | null>(null);
  const selectedExerciseRef   = useRef('squat');
  const videoRef        = useRef<HTMLVideoElement>(null);
  const streamRef       = useRef<MediaStream | null>(null);
  const captureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captureCanvas   = useRef<HTMLCanvasElement | null>(null);

  // ── Frame capture to server ───────────────────────────────────────────────

  const startCapture = useCallback(() => {
    if (!captureCanvas.current) {
      const c = document.createElement('canvas');
      // 640×480 — same aspect as camera request, better YOLO accuracy than 320×240
      c.width = 640; c.height = 480;
      captureCanvas.current = c;
    }

    const tick = () => {
      const video = videoRef.current;
      const ws    = wsRef.current;
      const cvs   = captureCanvas.current;
      if (!video || !cvs || ws?.readyState !== WebSocket.OPEN) {
        captureTimerRef.current = setTimeout(tick, 200);
        return;
      }
      if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        const ctx = cvs.getContext('2d')!;
        ctx.drawImage(video, 0, 0, 640, 480);
        const b64 = cvs.toDataURL('image/jpeg', 0.75).split(',')[1];
        ws.send(JSON.stringify({ type: 'client_frame', data: b64 }));
      }
      captureTimerRef.current = setTimeout(tick, 150); // ~7 fps to server
    };

    captureTimerRef.current = setTimeout(tick, 150);
  }, []);

  const stopCapture = useCallback(() => {
    if (captureTimerRef.current !== null) {
      clearTimeout(captureTimerRef.current);
      captureTimerRef.current = null;
    }
  }, []);

  // ── Camera ────────────────────────────────────────────────────────────────

  const startCamera = useCallback(async () => {
    setCameraErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
      startCapture();
    } catch {
      setCameraErr('摄像头权限被拒绝或设备不可用，将使用演示模式');
    }
  }, [startCapture]);

  const stopCamera = useCallback(() => {
    stopCapture();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, [stopCapture]);

  const sendExercise = useCallback((exerciseId: string) => {
    setSelectedExercise(exerciseId);
    selectedExerciseRef.current = exerciseId;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'set_exercise', exercise: exerciseId }));
    }
  }, []);

  // ── WebSocket ─────────────────────────────────────────────────────────────

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    setConnecting(true);
    
    console.log("实际 WebSocket 地址:", env.wsUrl);
    const ws = new WebSocket(env.wsUrl);

    ws.onopen = () => {
      setConnected(true);
      setConnecting(false);
      ws.send(JSON.stringify({ type: 'set_exercise', exercise: selectedExerciseRef.current }));
      startCamera();
    };
    ws.onmessage = (evt) => {
      try {
        const d = JSON.parse(evt.data);
        if (d.type === 'pose_data') {
          setPoseData(d as PoseData);
        } else if (d.type === 'exercise_ack' && d.ok) {
          setSelectedExercise(d.exercise);
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      setConnected(false);
      setConnecting(false);
      wsRef.current = null;
    };
    ws.onerror = () => {
      setConnected(false);
      setConnecting(false);
    };

    wsRef.current = ws;
  }, [startCamera]);

  const disconnect = useCallback(() => {
    stopCamera();
    wsRef.current?.close();
    setPoseData(null);
  }, [stopCamera]);

  useEffect(() => () => { wsRef.current?.close(); stopCamera(); }, [stopCamera]);

  // ── Derived display values ────────────────────────────────────────────────

  const score    = poseData?.score    ?? 0;
  const reps     = poseData?.reps     ?? 0;
  const duration = poseData?.duration ?? 0;
  const exercise = poseData?.exercise ?? selectedExercise;
  const feedback = poseData?.feedback ?? [];
  const isDemoMode = !!(poseData?.demo && !cameraOn);
  const displayAngles = Object.fromEntries(
    Object.entries(poseData?.angles ?? {}).filter(([k]) => k in ANGLE_LABELS)
  );
  const activeExerciseLabel = exerciseLabel(exercise);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-[var(--mx-bg)] text-[var(--mx-text)] overflow-hidden">

      {/* ── Header ── */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-[var(--mx-border)] shrink-0 bg-[var(--mx-bg-elevated)]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--mx-accent-soft)] border border-[var(--mx-accent-border)] flex items-center justify-center shrink-0">
            <span className="text-[var(--mx-accent)] font-bold text-xs tracking-tight">MX</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-lg tracking-tight text-[var(--mx-text)]">
              Motion<span className="text-[var(--mx-accent)]">X</span>
            </span>
            <span className="text-xs text-[var(--mx-text-muted)] uppercase tracking-widest hidden sm:block">
              AI Motion Intelligence
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          {isDemoMode && (
            <span className="text-xs text-[var(--mx-warning)] px-2.5 py-1 rounded-full border border-[rgba(201,165,92,0.25)] bg-[rgba(201,165,92,0.08)]">
              演示模式
            </span>
          )}
          {cameraErr && (
            <span className="text-xs text-[var(--mx-danger)] px-2.5 py-1 rounded-full border border-[rgba(193,127,127,0.25)] bg-[rgba(193,127,127,0.08)] max-w-48 truncate" title={cameraErr}>
              {cameraErr}
            </span>
          )}
          {connected && (
            <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${
              cameraOn
                ? 'border-[var(--mx-border)] bg-[var(--mx-surface)] text-[var(--mx-text-secondary)]'
                : 'border-[var(--mx-border-subtle)] bg-[var(--mx-bg-elevated)] text-[var(--mx-text-muted)]'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${cameraOn ? 'bg-[var(--mx-accent)]' : 'bg-[var(--mx-text-muted)]'}`} />
              {cameraOn ? '摄像头开启' : '摄像头关闭'}
            </div>
          )}
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${
            connected
              ? 'border-[var(--mx-accent-border)] bg-[var(--mx-accent-soft)] text-[var(--mx-accent)]'
              : 'border-[var(--mx-border-subtle)] bg-[var(--mx-bg-elevated)] text-[var(--mx-text-muted)]'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-[var(--mx-accent)] animate-pulse' : 'bg-[var(--mx-text-muted)]'}`} />
            {connected ? 'AI 已连接' : '未连接'}
          </div>

          {connected ? (
            <button
              onClick={disconnect}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-[var(--mx-border)] bg-[var(--mx-surface)] text-[var(--mx-text-secondary)] hover:text-[var(--mx-text)] hover:bg-[var(--mx-bg-panel)] transition-colors"
            >
              停止训练
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={connecting}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-[var(--mx-accent)] text-[var(--mx-bg)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {connecting ? '连接中...' : '开始训练'}
            </button>
          )}
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left: video + canvas overlay */}
        <div className="flex-1 relative bg-[var(--mx-stage-mid)] border-r border-[var(--mx-border)] flex items-center justify-center overflow-hidden">

          {/*
            4:3 inner container — keeps video and annotation canvas at identical
            aspect ratio so skeleton joints align perfectly with the live feed.
            The camera is requested at 640×480 (4:3) and keypoints are normalized
            to that frame, so the canvas buffer (960×720, also 4:3) maps 1-to-1.
          */}
          <div
            className="relative h-full rounded-xl border border-[var(--mx-border)] bg-[var(--mx-stage-top)] overflow-hidden shadow-sm"
            style={{ aspectRatio: '4/3', maxWidth: '100%' }}
          >

            {/* Camera feed — visible element, NOT display:none.
                display:none prevents the browser from decoding frames, causing
                ctx.drawImage() in the capture canvas to get black images. */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`absolute inset-0 w-full h-full object-fill transition-opacity duration-300 ${
                cameraOn ? 'opacity-100' : 'opacity-0'
              }`}
            />

            {/* Annotation overlay — transparent canvas drawn on top of the video */}
            <PoseCanvas data={poseData} videoRef={videoRef} cameraOn={cameraOn} />

            {/* ── Video HUD (only while connected and data available) ── */}
            {connected && poseData && (
              <>
                <div className="absolute top-4 left-4 z-10">
                  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full bg-[var(--mx-bg)]/80 backdrop-blur-md border border-[var(--mx-border)] text-[var(--mx-text-secondary)]">
                    <span className={`w-1.5 h-1.5 rounded-full ${exercise === 'none' ? 'bg-[var(--mx-text-muted)] animate-pulse' : 'bg-[var(--mx-accent)]'}`} />
                    {activeExerciseLabel}
                  </span>
                </div>

                {feedback[0] && (
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 max-w-[70%]">
                    <p className="text-xs text-center px-4 py-2 rounded-full bg-[var(--mx-bg)]/85 backdrop-blur-md border border-[var(--mx-border)] text-[var(--mx-text-secondary)] whitespace-nowrap overflow-hidden text-ellipsis">
                      {feedback[0]}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Not connected overlay — covers the full left panel */}
          {!connected && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[var(--mx-surface)] border border-[var(--mx-border)] flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--mx-text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-[var(--mx-text-secondary)] text-sm">点击「开始训练」连接 AI 服务并启动摄像头</p>
                <p className="text-[var(--mx-text-muted)] text-xs mt-1">确保 FastAPI 服务运行在 {env.serverHost}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: analytics panel */}
        <aside className="w-[340px] shrink-0 relative flex flex-col border-l border-[var(--mx-border)] bg-[var(--mx-bg-panel)] overflow-hidden">
          <div className="relative flex flex-col gap-3 p-5 overflow-y-auto flex-1">
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="mx-label">训练数据</p>
                <p className="text-sm font-medium text-[var(--mx-text)] mt-1">实时分析面板</p>
              </div>
              {connected && (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-[var(--mx-accent)] px-2 py-1 rounded-full bg-[var(--mx-accent-soft)] border border-[var(--mx-accent-border)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--mx-accent)] animate-pulse" />
                  LIVE
                </span>
              )}
            </div>

            <div className="mx-card p-4">
              <ExerciseSelector
                value={selectedExercise}
                onChange={sendExercise}
              />
            </div>

            <div className="mx-card p-5">
              <ScoreRing score={score} />
            </div>

            <StatsBar reps={reps} duration={duration} exercise={exercise} />

            <div className="mx-card p-4">
              <FeedbackPanel messages={feedback} connected={connected} />
            </div>

            {Object.keys(displayAngles).length > 0 && (
              <div className="mx-card p-4">
                <AnglePanel angles={displayAngles} labels={ANGLE_LABELS} />
              </div>
            )}

            <div className="mt-auto pt-3 flex items-center justify-between border-t border-[var(--mx-border-subtle)]">
              <span className="text-[10px] text-[var(--mx-text-muted)]">MotionX</span>
              <span className="text-[10px] text-[var(--mx-text-muted)] tabular-nums">
                {connected ? `${String(Math.floor(duration / 60)).padStart(2, '0')}:${String(duration % 60).padStart(2, '0')}` : '—:—'}
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
